import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CaretDown, Clock, Lock, PawPrint, Storefront, Trash } from '@phosphor-icons/react';
import { formatRelativeTime } from '@budget/core';
import { apiFetch } from '../auth/apiFetch';
import { ActionMenu } from '../components/ActionMenu';
import { Modal, ModalFooter } from '../components/Modal';

type LockVariant = 'clock' | 'petcare' | 'showings';

// "Kiosks" section on the Settings page. Lists every linked kiosk with its
// last-seen time and a Lock/Unlock toggle (the kiosk polls /api/kiosk/state
// and overlays a lock screen when isLocked flips).
//
// Add Kiosk (mint enrollment code) and Revoke (delete row) are admin-only
// — gated to the household admin so the partner account sees just the
// device list + lock toggle.

interface KioskDevice {
  id: number;
  label: string;
  createdAt: string;
  createdByUserId: number;
  lastSeenAt: string | null;
  isLocked: boolean;
  lockedAt: string | null;
  lockedByUserId: number | null;
  lockVariant: LockVariant;
}

interface DevicesResponse {
  devices: KioskDevice[];
}

interface EnrollStartResponse {
  code: string;
  expiresAt: string;
}

interface SettingsKiosksSectionProps {
  adminViewer: boolean;
}

export function SettingsKiosksSection({ adminViewer }: SettingsKiosksSectionProps) {
  const [devices, setDevices] = useState<KioskDevice[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [enrollment, setEnrollment] = useState<EnrollStartResponse | null>(null);
  const [enrollmentError, setEnrollmentError] = useState<string | null>(null);
  const [creatingCode, setCreatingCode] = useState(false);

  const reload = useCallback(async () => {
    try {
      const res = await apiFetch('/api/kiosk/devices');
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        setLoadError(`load ${res.status}: ${body.slice(0, 200)}`);
        return;
      }
      const json = (await res.json()) as DevicesResponse;
      setDevices(json.devices);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'load failed');
    }
  }, []);

  useEffect(() => {
    void reload();
    // Refresh the device list to keep the "Active" badge fresh, but only
    // when this tab is visible — when it's backgrounded the user can't
    // see the badge anyway, and every poll costs a Neon write (verifyAuth
    // updates users.last_seen_at on every authenticated request). The
    // cadence is also coarse (60s) since the kiosk's own last_seen_at
    // heartbeat only fires every 30 min on the server — over-polling here
    // doesn't move the badge any faster, it just burns DB time. Triggers
    // an immediate refresh on visibility change so the badge is current
    // whenever the user actually looks at the page.
    let t: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (t !== null) return;
      void reload();
      t = setInterval(() => void reload(), 60_000);
    };
    const stop = () => {
      if (t === null) return;
      clearInterval(t);
      t = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, [reload]);

  // Lock with an explicit screen variant, or unlock. The variant is ignored
  // by the API when isLocked is false, so unlock omits it.
  async function setLock(device: KioskDevice, isLocked: boolean, variant?: LockVariant) {
    setBusyId(device.id);
    try {
      const res = await apiFetch(`/api/kiosk/devices?id=${device.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isLocked ? { isLocked, lockVariant: variant } : { isLocked }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        setLoadError(`lock ${res.status}: ${body.slice(0, 200)}`);
        return;
      }
      await reload();
    } finally {
      setBusyId(null);
    }
  }

  async function revoke(device: KioskDevice) {
    const ok = window.confirm(
      `Revoke "${device.label}"?\n\nThe kiosk will lose access immediately and you'll need a new code to re-link it.`,
    );
    if (!ok) return;
    setBusyId(device.id);
    try {
      const res = await apiFetch(`/api/kiosk/devices?id=${device.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        setLoadError(`revoke ${res.status}: ${body.slice(0, 200)}`);
        return;
      }
      await reload();
    } finally {
      setBusyId(null);
    }
  }

  async function startEnrollment() {
    setCreatingCode(true);
    setEnrollmentError(null);
    try {
      const res = await apiFetch('/api/kiosk/enroll/start', { method: 'POST' });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        setEnrollmentError(`start ${res.status}: ${body.slice(0, 200)}`);
        return;
      }
      const json = (await res.json()) as EnrollStartResponse;
      setEnrollment(json);
    } catch (err) {
      setEnrollmentError(err instanceof Error ? err.message : 'start failed');
    } finally {
      setCreatingCode(false);
    }
  }

  return (
    <section className="settings-section">
      <h3 className="section-title">Kiosks</h3>
      <div className="card-flush">
        {devices === null && loadError === null && (
          <div className="settings-row">
            <div className="text-sm text-fg-secondary">Loading…</div>
          </div>
        )}
        {loadError && (
          <div className="settings-row">
            <div className="text-sm text-red">{loadError}</div>
          </div>
        )}
        {devices !== null && devices.length === 0 && (
          <div className="settings-row">
            <div className="flex-1 min-w-0">
              <div className="font-medium">No kiosks linked</div>
              {adminViewer && (
                <div className="text-sm text-fg-secondary">
                  Add a kiosk to display the dashboard on a wall-mounted screen.
                </div>
              )}
            </div>
            {adminViewer && (
              <button
                type="button"
                onClick={() => void startEnrollment()}
                disabled={creatingCode}
                className="btn btn-outline ml-4 shrink-0"
              >
                {creatingCode ? 'Generating…' : 'Add Kiosk'}
              </button>
            )}
          </div>
        )}
        {devices !== null &&
          devices.map((d, idx) => {
            // Active = polled within the last 15s (3× the 5s kiosk state
            // poll interval, so one dropped network call doesn't flicker
            // the badge). Useful when multiple device rows exist for the
            // same physical kiosk — the active one is the only row whose
            // lock/unlock actually reaches the screen, so the badge tells
            // the user which row to target.
            const isActive =
              d.lastSeenAt !== null &&
              Date.now() - new Date(d.lastSeenAt).getTime() < 15_000;
            // Status badge. Lock state takes priority over the live "Active"
            // ping: a locked kiosk reads as Pet Care (yellow) or Locked (red),
            // otherwise green Active while it's polling.
            const badge =
              d.isLocked && d.lockVariant === 'petcare'
                ? { label: 'Pet Care', text: 'text-yellow', dot: 'bg-yellow', bg: 'bg-yellow-soft' }
                : d.isLocked && d.lockVariant === 'showings'
                  ? { label: 'Showings', text: 'text-blue', dot: 'bg-blue', bg: 'bg-blue-soft' }
                  : d.isLocked
                    ? { label: 'Locked', text: 'text-red', dot: 'bg-red', bg: 'bg-red-soft' }
                    : isActive
                      ? { label: 'Active', text: 'text-green', dot: 'bg-green', bg: 'bg-green-soft' }
                      : null;
            return (
            <div
              key={d.id}
              className={`settings-row ${idx > 0 ? 'border-t border-border' : ''}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate inline-flex items-center gap-2">
                  <span className="truncate">{d.label}</span>
                  {badge && (
                    <span
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${badge.bg} ${badge.text} text-[0.625rem] font-semibold uppercase tracking-[0.08em] tabular flex-none`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
                      {badge.label}
                    </span>
                  )}
                </div>
                <div className="text-sm text-fg-secondary">
                  {d.lastSeenAt
                    ? `Last seen ${formatRelativeTime(d.lastSeenAt)}`
                    : 'Never seen'}
                  {d.isLocked && d.lockedAt && (
                    <span className="text-fg-tertiary">
                      {' · '}
                      Locked {formatRelativeTime(d.lockedAt)}
                    </span>
                  )}
                </div>
              </div>
              {d.isLocked ? (
                <button
                  type="button"
                  onClick={() => void setLock(d, false)}
                  disabled={busyId === d.id}
                  className="btn btn-outline ml-3 shrink-0"
                >
                  {busyId === d.id ? '…' : 'Unlock'}
                </button>
              ) : (
                // One Lock button → dropdown of screen variants. Each option
                // locks the kiosk to that specific screen.
                <LockMenu
                  disabled={busyId === d.id}
                  onPick={(variant) => void setLock(d, true, variant)}
                />
              )}
              {adminViewer && (
                <ActionMenu
                  ariaLabel={`Actions for ${d.label}`}
                  size="lg"
                  className="ml-2"
                  disabled={busyId === d.id}
                  items={[
                    {
                      label: 'Revoke',
                      danger: true,
                      disabled: busyId === d.id,
                      icon: <Trash size={16} weight="bold" aria-hidden="true" />,
                      onClick: () => void revoke(d),
                    },
                  ]}
                />
              )}
            </div>
            );
          })}
        {adminViewer && devices !== null && devices.length > 0 && (
          <div className="settings-row border-t border-border">
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => void startEnrollment()}
              disabled={creatingCode}
              className="btn btn-outline"
            >
              {creatingCode ? 'Generating…' : 'Add Kiosk'}
            </button>
          </div>
        )}
        {enrollmentError && (
          <div className="px-4 sm:px-5 py-2 text-xs text-red border-t border-border">
            {enrollmentError}
          </div>
        )}
      </div>

      {enrollment && (
        <EnrollmentCodeModal
          code={enrollment.code}
          expiresAt={enrollment.expiresAt}
          onClose={() => {
            setEnrollment(null);
            void reload();
          }}
        />
      )}
    </section>
  );
}

// Labeled "Lock" button that opens a dropdown of lock-screen variants.
// Mirrors ActionMenu's outside-click / Escape dismissal and elevated panel,
// but with a labeled trigger (icon + "Lock") instead of the kebab. Each option
// locks the kiosk to that screen. The 'showings' option is temporary — drop it
// (and migration 022's value) once the house sells.
const LOCK_OPTIONS: { variant: LockVariant; label: string; icon: typeof Lock }[] = [
  { variant: 'clock', label: 'Clock', icon: Clock },
  { variant: 'petcare', label: 'Pet Care', icon: PawPrint },
  { variant: 'showings', label: 'Showings', icon: Storefront },
];

function LockMenu({
  disabled,
  onPick,
}: {
  disabled: boolean;
  onPick: (variant: LockVariant) => void;
}) {
  const [open, setOpen] = useState(false);
  // The dropdown is portaled to <body> and fixed-positioned from the trigger's
  // rect: the row lives inside `.card-flush` (overflow:hidden, to clip its
  // rounded corners), which would otherwise clip an in-flow absolute menu.
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        !triggerRef.current?.contains(t) &&
        !menuRef.current?.contains(t)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // Fixed positioning doesn't track scroll/resize, so just dismiss on either.
    const onReflow = () => setOpen(false);
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [open]);

  function toggle() {
    if (!open && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
    }
    setOpen((v) => !v);
  }

  return (
    <div className="ml-3 inline-flex shrink-0">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={toggle}
        className="btn btn-outline shrink-0 gap-1.5"
      >
        <Lock size={15} weight="bold" aria-hidden="true" />
        {disabled ? '…' : 'Lock'}
        <CaretDown size={13} weight="bold" aria-hidden="true" />
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ position: 'fixed', top: pos.top, right: pos.right }}
            className="z-50 min-w-40 bg-elevated border border-border rounded-lg shadow-lg py-1"
          >
            {LOCK_OPTIONS.map(({ variant, label, icon: Icon }) => (
              <button
                key={variant}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onPick(variant);
                }}
                className="w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 text-fg-secondary hover:bg-overlay"
              >
                <Icon size={16} weight="bold" aria-hidden="true" />
                {label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

interface EnrollmentCodeModalProps {
  code: string;
  expiresAt: string;
  onClose: () => void;
}

function EnrollmentCodeModal({ code, expiresAt, onClose }: EnrollmentCodeModalProps) {
  const [copied, setCopied] = useState(false);
  const expiresAtMs = new Date(expiresAt).getTime();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const secondsLeft = Math.max(0, Math.floor((expiresAtMs - now) / 1000));
  const mm = Math.floor(secondsLeft / 60);
  const ss = secondsLeft % 60;

  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore; copy is a convenience
    }
  }

  return (
    <Modal onClose={onClose} size="sm">
      <div className="p-6 flex flex-col gap-4">
        <div>
          <h2 className="text-lg font-semibold">Link a kiosk</h2>
          <p className="text-sm text-fg-secondary mt-1">
            On the kiosk, type this code in the "Link this kiosk" screen.
          </p>
        </div>
        <div className="card flex flex-col items-center gap-2 py-6">
          <code className="text-3xl tabular tracking-widest font-medium select-all">
            {code}
          </code>
          <span className="text-xs text-fg-tertiary">
            Expires in {mm}:{ss.toString().padStart(2, '0')}
          </span>
        </div>
        <button type="button" onClick={() => void copy()} className="btn btn-outline">
          {copied ? 'Copied' : 'Copy code'}
        </button>
      </div>
      <ModalFooter>
        <button type="button" onClick={onClose} className="btn btn-primary">
          Done
        </button>
      </ModalFooter>
    </Modal>
  );
}
